import React from 'react';

import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';

import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { max, range } from 'lodash';
import zip from 'lodash/zip';
import {
  CartesianGrid,
  Label,
  ReferenceLine,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';
import { ISize, ITest, ITimingEvent } from '../../../data/types';
import { roundValue } from '../../../utils/math';
import { Column } from '../../layout/Column';
import { GraphContainer } from '../../shared/graphs/GraphContainer';
import { EPdfMode, isPdf, shouldInvert } from '../../shared/pdf/Pdf';
import { Card } from '../card/Card';
import { CardHeader } from '../card/CardHeader';
import { PhotoExportMenu } from './PhotoExportMenu';
import { CustomTooltip } from './Tooltip';

interface IDataPoint {
  [key: string]: number;
  Time: number;
  Force: number;
  Hertz: number;
}

interface IProps {
  loading: boolean;
  pdfMode: EPdfMode;
  style?: React.CSSProperties;
  force: number[];
  secondaryData: number[];
  secondaryLabel: string;
  secondaryUnits: string;
  roundingPrecision: number;
  timingEvents: ITimingEvent[];
  secondaryTickSize: number;
  startTime: number;
  test: ITest;
  hertz: number;
}

export const ForceProfile = (props: IProps): React.ReactElement => {
  const onExportPhoto = (backgroundColor?: string): void => {
    const { test } = props;
    html2canvas(
      document.querySelector(`.export-container-${props.secondaryLabel}`),
      {
        backgroundColor,
      }
    ).then((canvas: HTMLCanvasElement): void => {
      setTimeout((): void => {
        saveAs(
          canvas.toDataURL(),
          `${test.athlete.name}-${test.testType.name}.png`
        );
      }, 1000);
    });
  };

  const data: IDataPoint[] = React.useMemo((): IDataPoint[] => {
    return zip(props.force, props.secondaryData).map(
      (point: number[], index: number): IDataPoint => {
        return {
          Time: props.startTime + index,
          Force: point[0] ?? 0,
          [props.secondaryLabel]: point[1] ?? 0,
          Hertz: props.hertz,
        };
      }
    );
  }, [props.force, props.secondaryData]);

  const maxForce = max(props.force);
  const yMax = 1000 - (maxForce % 1000) + maxForce;
  const yTicks = range(yMax / 1000 + 1).map(
    (tick: number): number => tick * 1000
  );

  const [secondaryMin, secondaryMax] = minMax(props.secondaryData);

  const xMax =
    props.secondaryTickSize -
    (secondaryMax % props.secondaryTickSize) +
    secondaryMax;
  const xMin =
    props.secondaryTickSize -
    (secondaryMin % props.secondaryTickSize) +
    secondaryMin;
  const xTicks: number[] = [];
  for (
    let i = xMin - props.secondaryTickSize;
    i <= xMax;
    i += props.secondaryTickSize
  ) {
    xTicks.push(i);
  }

  return (
    <ForceTimeCard
      style={{
        ...props.style,
        flex: 3,
      }}
    >
      <Column>
        <CardHeader>
          <HeaderLeft>
            <h6>{`Force-${props.secondaryLabel}`}</h6>
          </HeaderLeft>
          <HeaderRight>
            {!isPdf(props.pdfMode) && (
              <PhotoExportMenu onExportPhoto={onExportPhoto} />
            )}
          </HeaderRight>
        </CardHeader>

        {!props.loading && data.length > 0 ? (
          <GraphContainer inverse={shouldInvert(props.pdfMode)}>
            <AutoSizer>
              {(size: ISize): React.ReactElement => {
                return (
                  <ScatterChart
                    width={size.width}
                    height={size.height}
                    className={`export-container-${props.secondaryLabel}`}
                  >
                    <CartesianGrid
                      stroke='rgba(255,255,255,.2)'
                      strokeWidth={0.25}
                    />
                    <XAxis
                      dataKey={props.secondaryLabel}
                      type='number'
                      tickCount={10}
                      domain={[xMin, xMax]}
                      tickFormatter={tickFormatterFactory(
                        props.roundingPrecision
                      )}
                      ticks={xTicks}
                      tick={{ fill: 'var(--color-text' }}
                      axisLine={false}
                      tickLine={false}
                    >
                      <Label
                        position='insideBottom'
                        offset={-2.5}
                        value={`${props.secondaryLabel} (${props.secondaryUnits})`}
                        style={{ fill: 'var(--color-text)' }}
                      />
                    </XAxis>
                    <ReferenceLine
                      yAxisId='left'
                      x={0}
                      ifOverflow='extendDomain'
                      stroke='rgba(255,255,255,.2)'
                      strokeWidth={3}
                      strokeOpacity={1}
                    />

                    <YAxis
                      yAxisId='left'
                      type='number'
                      tickCount={10}
                      domain={[0, yMax]}
                      ticks={yTicks}
                      orientation='left'
                      dataKey='Force'
                      tick={{ fill: 'var(--color-text' }}
                      axisLine={false}
                      tickLine={false}
                    >
                      <Label
                        position='insideLeft'
                        offset={5}
                        angle={-90}
                        value={'Force (N)'}
                        style={{ fill: 'var(--color-text)' }}
                      />
                    </YAxis>
                    <Tooltip
                      content={CustomTooltip}
                      cursor={{ strokeDasharray: '3 3' }}
                    />

                    <Scatter
                      name=''
                      data={data}
                      shape={CustomDotFactory(props.timingEvents)}
                      fill={
                        props.pdfMode === EPdfMode.PRINT
                          ? 'lightblue'
                          : 'lightblue'
                      }
                      line={false}
                      isAnimationActive={false}
                      yAxisId='left'
                    />
                  </ScatterChart>
                );
              }}
            </AutoSizer>
          </GraphContainer>
        ) : null}
      </Column>
    </ForceTimeCard>
  );
};

const minMax = (values: number[]): [number, number] => {
  let low = Number.POSITIVE_INFINITY;
  let high = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    if (value < low) low = value;
    if (value > high) high = value;
  }
  return [low, high];
};

const tickFormatterFactory =
  (precision: number): ((tick: string) => string) =>
  (tick: string): string =>
    `${roundValue(+tick, precision)}`;

const CustomDotFactory = (
  timingEvents: ITimingEvent[]
): ((props: any) => React.ReactElement) => {
  const CustomDot = (props: any): React.ReactElement => {
    const timingEvent = timingEvents.find((event: ITimingEvent): boolean => {
      return props.Time >= event.start && props.Time <= event.end;
    });

    return (
      <g id={`point-${props.Time}`}>
        <circle
          style={{ cursor: 'pointer' }}
          cx={props.cx}
          cy={props.cy}
          fill={timingEvent?.color ?? 'transparent'}
          height={props.height}
          stroke={'transparent'}
          width={props.width}
          y={props.y}
          x={props.x}
          r={2}
        ></circle>
      </g>
    );
  };
  return CustomDot;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ForceTimeCard = styled(Card as any)`
  padding: 0px;
  margin-top: var(--grid-gap);
  min-height: var(--graph-min-height);
`;
const HeaderLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;

const HeaderRight = styled.div`
  flex: 0;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;
